/**
 * Flash Sale Promotion Templates
 * Time-limited offers that create urgency and drive immediate action
 */

import { PromotionTemplate } from "./types";

export const FLASH_SALE_TEMPLATES: PromotionTemplate[] = [
  {
    id: "24-hour-flash",
    name: "24-Hour Flash Sale",
    category: "flash_sale",
    description: "Create extreme urgency with a one-day-only offer",
    strategy: "Flash sales leverage scarcity and FOMO. The short window forces quick decisions. Best used sparingly (quarterly) to maintain impact. Announce via email beforehand for maximum reach.",
    icon: "ThunderboltOutlined",
    color: "#EF4444",
    expectedOutcomes: [
      "5-10x daily conversion rate",
      "Large revenue spike in 24 hours",
      "Email list engagement boost",
    ],
    bestPractices: [
      "Announce 24 hours before via email",
      "Use prominent countdown timer",
      "Make discount significant (40%+)",
      "Don't extend - maintain credibility",
    ],
    tags: ["flash-sale", "urgency", "limited-time", "24-hour"],
    bestFor: ["All SaaS", "Products with email lists", "Engaged user bases"],
    difficulty: "beginner",
    setupTime: "10 min",
    defaults: {
      type: "popup",
      displayStyle: "bold",
      priority: 10,
      targetAudience: ["free", "trial"],
      discount: {
        discountType: "percentage",
        discountValue: 50,
        currency: "USD",
        applicablePlans: ["pro", "starter"],
      },
      styling: {
        theme: "dark",
        primaryColor: "#EF4444",
        secondaryColor: "#F97316",
        animation: "pulse",
      },
      displayConditions: {
        showOnPages: ["all"],
        maxImpressionsPerUser: 10,
        delaySeconds: 0,
      },
      urgency: {
        showCountdown: true,
        stockLimit: 0,
      },
      gamification: {
        type: "none",
      },
      content: {
        en: {
          headline: "24-HOUR FLASH SALE",
          subheadline: "50% off ends at midnight. Don't miss out!",
          ctaText: "Grab the Deal",
          ctaSecondaryText: "View Offer",
          badge: "24 HOURS ONLY",
          terms: "*Offer expires at 11:59 PM tonight.",
        },
      },
    },
  },
  {
    id: "weekend-blitz",
    name: "Weekend Blitz",
    category: "flash_sale",
    description: "Weekend-only sale for users with more browsing time",
    strategy: "Weekends see higher engagement as users have more time to explore. A Friday-Sunday sale captures this attention window while maintaining urgency.",
    icon: "CalendarOutlined",
    color: "#8B5CF6",
    expectedOutcomes: [
      "2-3x weekend conversion rate",
      "Higher engagement from casual users",
      "Weekend revenue boost",
    ],
    bestPractices: [
      "Start Friday afternoon",
      "End Sunday midnight",
      "Target users who engage on weekends",
      "Use relaxed, friendly messaging",
    ],
    tags: ["flash-sale", "weekend", "limited-time"],
    bestFor: ["Consumer apps", "Side projects", "Creative tools"],
    difficulty: "beginner",
    setupTime: "10 min",
    defaults: {
      type: "banner",
      displayStyle: "gradient",
      priority: 8,
      targetAudience: ["free", "trial"],
      discount: {
        discountType: "percentage",
        discountValue: 40,
        currency: "USD",
        applicablePlans: ["pro", "starter"],
      },
      styling: {
        theme: "gradient_purple",
        primaryColor: "#8B5CF6",
        secondaryColor: "#EC4899",
        animation: "fade",
      },
      displayConditions: {
        showOnPages: ["dashboard", "pricing"],
        maxImpressionsPerUser: 5,
        delaySeconds: 2,
      },
      urgency: {
        showCountdown: true,
        stockLimit: 0,
      },
      gamification: {
        type: "none",
      },
      content: {
        en: {
          headline: "Weekend Special!",
          subheadline: "40% off this weekend only. Treat yourself!",
          ctaText: "Get Weekend Deal",
          ctaSecondaryText: "Learn More",
          badge: "THIS WEEKEND",
          terms: "*Valid Friday-Sunday only.",
        },
      },
    },
  },
  {
    id: "spin-to-win",
    name: "Spin to Win",
    category: "flash_sale",
    description: "Gamified flash sale with a spin wheel for random discounts",
    strategy: "Gamification increases engagement and time on site. The randomness creates excitement and social sharing. Even 'losing' spins can offer small discounts to maintain conversions.",
    icon: "TrophyOutlined",
    color: "#10B981",
    expectedOutcomes: [
      "3-5x engagement rate",
      "Viral sharing potential",
      "Higher email capture",
      "Memorable brand experience",
    ],
    bestPractices: [
      "Ensure all outcomes are wins (10-50% off)",
      "Require email for spin",
      "Limit to one spin per user",
      "Make the experience visually exciting",
    ],
    tags: ["flash-sale", "gamification", "spin-wheel", "interactive"],
    bestFor: ["Consumer apps", "Young demographics", "Social products"],
    difficulty: "intermediate",
    setupTime: "25 min",
    defaults: {
      type: "gamified",
      displayStyle: "bold",
      priority: 9,
      targetAudience: ["free", "trial", "new"],
      discount: {
        discountType: "percentage",
        discountValue: 30,
        currency: "USD",
        applicablePlans: ["pro", "starter"],
      },
      styling: {
        theme: "gradient_purple",
        primaryColor: "#10B981",
        secondaryColor: "#8B5CF6",
        animation: "bounce",
      },
      displayConditions: {
        showOnPages: ["dashboard", "pricing"],
        maxImpressionsPerUser: 3,
        delaySeconds: 5,
      },
      urgency: {
        showCountdown: true,
        stockLimit: 0,
      },
      gamification: {
        type: "spin_wheel",
        wheelSegments: [
          { label: "50% OFF", value: 50, probability: 10 },
          { label: "40% OFF", value: 40, probability: 15 },
          { label: "30% OFF", value: 30, probability: 25 },
          { label: "20% OFF", value: 20, probability: 30 },
          { label: "10% OFF", value: 10, probability: 20 },
        ],
      },
      content: {
        en: {
          headline: "Spin to Win!",
          subheadline: "Try your luck! Spin the wheel for up to 50% off.",
          ctaText: "Spin Now",
          ctaSecondaryText: "No Thanks",
          badge: "LUCKY SPIN",
          terms: "*One spin per user. Prize valid for 24 hours.",
        },
      },
    },
  },
  {
    id: "scratch-card-sale",
    name: "Scratch & Save",
    category: "flash_sale",
    description: "Interactive scratch card revealing surprise discounts",
    strategy: "Scratch cards tap into lottery psychology. The reveal moment creates dopamine hits. Users feel they 'discovered' their discount, increasing perceived value.",
    icon: "CreditCardOutlined",
    color: "#F59E0B",
    expectedOutcomes: [
      "High engagement rates",
      "Increased time on site",
      "Social sharing of wins",
    ],
    bestPractices: [
      "Make scratching satisfying",
      "Reveal discount dramatically",
      "All cards should be winners",
      "Add confetti on reveal",
    ],
    tags: ["flash-sale", "gamification", "scratch-card", "interactive"],
    bestFor: ["E-commerce", "Consumer apps", "Mobile-first products"],
    difficulty: "intermediate",
    setupTime: "25 min",
    defaults: {
      type: "gamified",
      displayStyle: "bold",
      priority: 8,
      targetAudience: ["free", "trial"],
      discount: {
        discountType: "percentage",
        discountValue: 25,
        currency: "USD",
        applicablePlans: ["pro", "starter"],
      },
      styling: {
        theme: "default",
        primaryColor: "#F59E0B",
        secondaryColor: "#EF4444",
        animation: "shake",
      },
      displayConditions: {
        showOnPages: ["pricing"],
        maxImpressionsPerUser: 2,
        delaySeconds: 3,
      },
      urgency: {
        showCountdown: false,
        stockLimit: 50,
      },
      gamification: {
        type: "scratch_card",
      },
      content: {
        en: {
          headline: "Scratch & Save!",
          subheadline: "Reveal your mystery discount. Up to 50% off!",
          ctaText: "Scratch Now",
          ctaSecondaryText: "Maybe Later",
          badge: "MYSTERY DEAL",
          terms: "*One scratch per user. Discount valid for 48 hours.",
        },
      },
    },
  },
  {
    id: "limited-seats",
    name: "Limited Seats Sale",
    category: "flash_sale",
    description: "Scarcity-driven sale with limited number of discounts",
    strategy: "Real scarcity (not fake countdown timers) builds trust while creating urgency. When users see 'Only 23 left', they act fast. Update count in real-time for authenticity.",
    icon: "TeamOutlined",
    color: "#6366F1",
    expectedOutcomes: [
      "Rapid conversion velocity",
      "Real urgency without gimmicks",
      "Trust building through authenticity",
    ],
    bestPractices: [
      "Use real inventory counts",
      "Update in real-time",
      "Start with believable number (50-100)",
      "Honor the limit strictly",
    ],
    tags: ["flash-sale", "scarcity", "limited-quantity"],
    bestFor: ["Premium products", "Cohort-based products", "Courses"],
    difficulty: "intermediate",
    setupTime: "15 min",
    defaults: {
      type: "popup",
      displayStyle: "standard",
      priority: 9,
      targetAudience: ["free", "trial"],
      discount: {
        discountType: "percentage",
        discountValue: 45,
        currency: "USD",
        applicablePlans: ["pro"],
      },
      styling: {
        theme: "default",
        primaryColor: "#6366F1",
        secondaryColor: "#8B5CF6",
        animation: "fade",
      },
      displayConditions: {
        showOnPages: ["dashboard", "pricing"],
        maxImpressionsPerUser: 5,
        delaySeconds: 2,
      },
      urgency: {
        showCountdown: false,
        stockLimit: 50,
      },
      gamification: {
        type: "none",
      },
      content: {
        en: {
          headline: "Limited Seats Available",
          subheadline: "Only 50 spots at 45% off. When they're gone, they're gone.",
          ctaText: "Claim My Spot",
          ctaSecondaryText: "View Details",
          badge: "LIMITED",
          terms: "*Discount applies to first 50 subscribers only.",
        },
      },
    },
  },
];
